/* Copyright (c) 2016-2025 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";
import MediaUploaderViewModel from "../ui/shared/media-uploader.js";

const program = {
	code: "reho",
	name: "Regent Honeyeater",
	description: `
		<p>
			The Regent Honeyeater is a critically endangered bird that inhabits the temperate woodlands of south-eastern Australia. Formerly found from Adelaide to south-east Queensland, the species’ range is now heavily contracted and today is primarily restricted to NSW, but with small populations persisting in south-east Queensland and north-east Victoria. With fewer than 300 individuals remaining in the wild, it is imperative that we have up to date information on the status and habitat use of the species. Many birds bred in captivity have been released into the wild to supplement the wild population and we need to track the survival and movements of these birds.
		</p>
		<p>
			The Regent Honeyeater module in Birdata is a citizen science project of BirdLife Australia’s Woodland Birds Program. The goal of the project is to encourage the public to search for and report sightings of Regent Honeyeaters and to gather information on habitat conditions - including details of areas where no Regent Honeyeaters are found. This information will help us monitor the population, and assist in identifying, protecting and restoring important Regent Honeyeater habitat.
		</p>
		<p>
			We encourage you to undertake a thorough search of suitable Regent Honeyeater habitat, using the 500 m area search method for at least 20 minutes duration. You should focus your search on blossoming trees and shrubs within your search area, especially where other honeyeaters or lorikeets are present. Alternatively, use the incidental search option if you are doing a smaller, quicker search or if you see a Regent Honeyeater opportunistically. If you are unfamiliar with Regent Honeyeaters, there are several linked resources below that you can review before you commence your search. Should you find Regent Honeyeaters we encourage you to take photos of the individuals and also look for colour bands on the legs, which are present on all released zoo-bred birds and some wild birds.
		</p>
		<p>
		Contact: <a href="mailto:woodlandbirds@birdlife.org.au">woodlandbirds@birdlife.org.au</a><br>
		Website: <a href="https://birdlife.org.au/programs/woodland-birds/">https://birdlife.org.au/programs/woodland-birds/</a>
		</p>
		`,
	load() {
		return birdata.lookup().then(function(lookups) {
			program.options = lookups;
			program.surveyTypeOptions = program.options.survey_type.filter(st =>
				st.code == 'incidental_search' || st.code == 'area_search_within_500_m');
		});
	},
	canParticipate(user) {
		return true;
	},
	canFilterBy(user) {
		return user.is_administrator || user.permissions?.some(function(p) {
			return p.type == "program" && p.program == "reho";
		});
	},
	fields: function() {
		let fields = [
			{
				name: "treeShrubFlowering",
				label: "Trees and shrubs flowering?",
				type: "options",
				options: program.options.reho_tree_shrub_flowering,
				help: "<p><a href='https://www.calameo.com/read/004107895b64799ac336c' target='_blank'>View Eucalypt identification guide</a></p>",
			},
			{
				name: "treeSpecies",
				label: "Flowering tree/shrub species",
				type: "options",
				options: program.options.reho_tree_species,
				sortOptions: false,
				multiselect: true,
				help: "<p><a href='https://www.calameo.com/read/004107895b64799ac336c' target='_blank'>View Eucalypt identification guide</a></p>",
				hidden: () => !["yes", "budding", "budding_and_flowering"].includes(fields.treeShrubFlowering()?.code)
			},
			{
				name: "otherTreeSpecies",
				label: "Other flowering tree/shrub species",
				type: "text",
				hidden: () => fields.treeSpecies.field.hidden() || !fields.treeSpecies().some(x => x.code === 'other')
			},
			{
				name: "treePhotos",
				label: "Tree/shrub photos",
				key: "tree_media_uuids",
				type: "media"
			},
			{
				name: "mistletoeStatus",
				type: "options",
				options: program.options.reho_mistletoe_status,
				help: "<p><a href='https://www.calameo.com/read/00410789579ce6e27297d' target='_blank'>View Mistletoe identification guide</a></p>"
			},
			{
				name: "mistletoeSpecies",
				label: "What mistletoe is flowering?",
				type: "options",
				options: program.options.reho_mistletoe_species,
				help: "<p><a href='https://www.calameo.com/read/00410789579ce6e27297d' target='_blank'>View Mistletoe identification guide</a></p>",
				hidden: () => fields.mistletoeStatus()?.code !== 'present_flowering'
			},
			{
				name: "mistletoePhotos",
				key: "mistletoe_media_uuids",
				type: "media"
			},
			{
				label: "Is lerp present within the search area?",
				name: "lerpStatus",
				type: "text",
				options: ["Present – Light/Moderate", "Present – Heavy", "Unsure", "Absent"],
				help: `
				<p style = 'margin-bottom: 1em;'>’Lerp’ are the sugar-rich casings attached to eucalypt leaves by sap-sucking insects called ‘psyllids’. These casings are white in colour and up to 5mm in size, and are made up of solidified honeydew and wax.</p>
				<ul>
					<li><b>Light/Moderate</b>: The foliage of most or all trees contain only minimal to moderate amounts of lerp – OR – the foliage of most or all trees do not contain lerp at all, with a small proportion of existing trees containing light, moderate or even heavy amounts of lerp.</li>
					<li><b>Heavy</b>: Lerp is abundantly available, with the foliage of most or all trees containing a heavy amount – OR – the foliage of most or all trees contain a light to moderate amount of lerp, with a small proportion of existing trees containing heavy amount of lerp.</li>
					<li><b>Unsure</b>: Survey conditions (e.g. weather, available light etc.) made it difficult to detect lerp – OR – the observer is unfamiliar with identifying lerp.</li>
					<li><b>Absent</b>: No trees within the search area were seen to bear lerp among their foliage.</li>
				</ul>`
			},
			{
				name: "freshwaterPresent",
				help: "Is there any freshwater present within the search area at the time of survey?",
				label: "Is freshwater present within search area?",
				type: "boolean",
				allowNull: true,
				optionLabels: ["Yes", "No", "Select…"],
				defaultValue: null
			},
			{
				name: "habitatPhotos",
				label: "General habitat photos",
				key: "habitat_media_uuids",
				type: "media"
			},
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, f => f.field.name);

		return fields;

	},
	sightingFields: function(sightingModel) {
		let isReho = sightingModel.species.common_name == 'Regent Honeyeater';

		let fields = [
			{
				name: "bandsPresent",
				label: "Did it have leg bands?",
				type: "options",
				options: [
					{ name: "Yes", code: "yes"},
					{ name: "No", code: "no"},
					{ name: "Unsure", code: "unsure"}
				],
				required: true,
				validate: 'required',
				hidden: () => !isReho
			},
			{
				name: "bandsDescription",
				type: "text",
				multiline: true,
				placeholder: "Some Regent Honeyeaters have colour bands on their legs. If you see leg bands, please record as much of the band combination as you can, for example orange over blue on left leg, red over metal on right leg",
				help: `<img src="${birdata.APP_ROOT_URL}images/reho_bands.jpg" style="max-width: 100%;">`,
				hidden: () => !isReho || fields.bandsPresent()?.code !== "yes"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, f => f.field.name);

		return fields;
	},
	programData: function(survey) {
		let fields = program.fields(survey);

		return { fields };
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().reho.fields;
		json.reho = {};

		for(let f of Object.values(fields)) {
			f.field.save(json.reho);
		}
	},
	preprocessJSON: function(json) {
		if(!json.reho) return;

		if(json._preprocessed) return;
		json._preprocessed = true;

		for(let sighting of (json.sightings || [])) {
			let data = sighting.reho;
			if(data) {
				if(data.bands_present === true) {
					data.bands_present = "yes"
				} else if(data.bands_present === false) {
					data.bands_present = "no"
				} else {
					data.bands_present = "unsure";
				}
			} else {
				sighting.reho = {};
			}
		}
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.reho;

		if(!data) return; //incomplete survey

		this.preprocessJSON(json);

		for(let f of Object.values(fields)) {
			f.field.load(data);
		}
	},
	updateSightingJSON: function(json, sighting, survey) {
		let data = json.reho;
		if(data.bands_present == "yes") {
			data.bands_present = true;
		} else if(data.bands_present == "no") {
			data.bands_present = false;
		} else {
			data.bands_present = null;
		}
	},
	visibleSharedSiteLayers: () => [],
	defaultAreaLayer: (layers) => null,
	filterAreaLayers: (layer) => false,
	hideWaterLevel: true,
	hideSurveyType: false,
	surveyTypeOptions: null, // This gets set in load()
	afterSelect: function(survey) {
		if(!this.surveyTypeOptions.includes(survey.type())) {
			survey.type(null)
		}
	},
	beforeDeselect: function(survey) {
	},
	canAssignSurveyOwner: true,
	allSpeciesRecordedEnabled: () => true,
	multipleDaysEnabled: () => true,
	observerCountEnabled: () => true,
	observerCountIsOptional: () => true,
	sightingLocations: false,
	forceSightingLocations: false,
	multipleSightingsPerSpecies: false,
	validateDuration(duration, survey) {
		// Survey duration is always required (even for incidental_search)
		if(!duration) {
			return "Survey duration is required";
		}
	}
};

export default program;
